/**
 * i18n. Adding a language = add a file that satisfies `Dict`, register it below.
 * Burmese (my) is planned: the picker shows it as "coming soon" until a native
 * speaker has reviewed the health wording.
 */
import type { Locale } from '../domain/types';
import { th, type Dict } from './th';
import { en } from './en';

export const LANGS: { code: Locale; label: string; available: boolean }[] = [
  { code: 'th', label: 'ไทย', available: true },
  { code: 'en', label: 'English', available: true },
  { code: 'my', label: 'မြန်မာ', available: false }
];

const dicts: Partial<Record<Locale, Dict>> = { th, en };
let current: Locale = 'th';
let dict: Dict = th;

export function setLocale(l: Locale): void {
  current = dicts[l] ? l : 'en';
  dict = dicts[current] ?? th;
  document.documentElement.lang = current;
}
export const getLocale = (): Locale => current;

/** Best guess from the browser before the user chooses. */
export function detectLocale(): Locale {
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const l of langs) {
    const c = l.slice(0, 2).toLowerCase();
    if (c === 'th') return 'th';
    if (c === 'my') return 'my';
    if (c === 'en') return 'en';
  }
  return 'th';
}

type Section = keyof Dict;
/** t('home', 'countingTitle', { n: 7 }) */
export function t<S extends Section>(section: S, key: keyof Dict[S] & string, vars?: Record<string, string | number>): string {
  const raw = (dict[section] as Record<string, string>)[key] ?? (th[section] as Record<string, string>)[key] ?? key;
  return vars ? raw.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? '')) : raw;
}

export function fmtDate(ms: number, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string {
  return new Date(ms).toLocaleDateString(dict.meta.dateLocale, opts);
}
export function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString(dict.meta.dateLocale, { hour: '2-digit', minute: '2-digit' });
}
export function fmtDuration(ms: number): string {
  const total = Math.max(1, Math.ceil(ms / 60000));
  const h = Math.floor(total / 60), m = total % 60;
  return h ? `${h} ${t('common', 'hours')} ${m} ${t('common', 'minutes')}` : `${m} ${t('common', 'minutes')}`;
}
